import React from 'react';
import VencedorDestaque from './VencedorDestaque';
import GraficoResultados from './GraficoResultados';
import EstatisticasGerais from './EstatisticasGerais';
import { AlertCircle } from 'lucide-react';

/**
 * Componente reutilizável para exibir resultados de enquetes
 * 
 * @param {Object} props
 * @param {Array} props.locais - Array de objetos com {local, nome, votos, percentual}
 * @param {Number} props.totalVotos - Total de votos da enquete
 * @param {String} props.dataEncerramento - Data de encerramento (ISO string)
 * @param {String} props.titulo - Título da enquete (opcional)
 * @param {String} props.descricao - Descrição da enquete (opcional)
 * @param {Boolean} props.loading - Estado de carregamento
 * @param {String} props.error - Mensagem de erro
 * @param {Function} props.onRetry - Callback para tentar novamente
 */
const ResultadoEnquete = ({
  locais = [],
  totalVotos = 0,
  dataEncerramento,
  titulo = 'Resultado Final da Votação',
  descricao,
  loading = false,
  error = null,
  onRetry
}) => {
  // Estado de loading
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg">Carregando resultados...</p>
      </div>
    );
  }

  // Estado de erro
  if (error) {
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-red-900 mb-2">Erro ao Carregar Resultados</h3>
        <p className="text-red-700 mb-4">{error}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Tentar Novamente
          </button>
        )}
      </div>
    );
  }

  // Sem dados
  if (!locais || locais.length === 0) {
    return (
      <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-8 text-center">
        <p className="text-gray-600 text-lg">Nenhum resultado disponível ainda.</p>
      </div>
    );
  }

  // Identifica o vencedor (maior número de votos)
  const vencedor = locais.reduce((prev, current) => 
    (prev.votos > current.votos) ? prev : current
  );

  return (
    <div className="max-w-5xl mx-auto">
      {/* Título e descrição */}
      {titulo && (
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
            {titulo}
          </h2>
          {descricao && (
            <p className="text-lg text-gray-600">
              {descricao}
            </p>
          )}
        </div>
      )}

      {/* Card do Vencedor */}
      <VencedorDestaque 
        vencedor={vencedor} 
        totalVotos={totalVotos} 
      />

      {/* Gráfico de Resultados */}
      <GraficoResultados 
        locais={locais} 
        totalVotos={totalVotos} 
      />

      {/* Estatísticas Gerais */}
      <EstatisticasGerais 
        totalVotos={totalVotos}
        dataEncerramento={dataEncerramento}
        vencedor={vencedor}
        locais={locais}
      />
    </div>
  );
};

export default ResultadoEnquete;
